/**
 * GraphQL Client Configuration (urql)
 *
 * Provides GraphQL client for profile mutations with automatic
 * authentication token injection. Uses urql for Refine.dev compatibility.
 *
 * Security Note: `credentials: "include"` sends cookies with all GraphQL
 * requests. This is required for session management with the backend.
 * The backend CORS configuration restricts requests to authorized origins.
 */
import { getAccessToken } from "@/providers/auth/token-provider";

import { Client, type CombinedError, cacheExchange, fetchExchange, mapExchange } from "@urql/core";
import { gql } from "@urql/core";

// Validate GraphQL URL is configured
const graphqlUrl = import.meta.env.VITE_GRAPHQL_URL;
if (!graphqlUrl) {
  throw new Error("VITE_GRAPHQL_URL environment variable is required for GraphQL client");
}

/** Code Engage sends when the session token is missing, invalid, expired or revoked. */
export const UNAUTHENTICATED_CODE = "UNAUTHENTICATED";

let unauthenticatedHandler: (() => void) | null = null;

/**
 * Sets what happens when Engage rejects the session token. App.tsx registers the same
 * refresh-or-redirect handling the FHIR client uses. This is a setter rather than an import
 * because the auth provider imports this module.
 * @param handler Called for each operation Engage rejects, or null to clear it
 */
export const setUnauthenticatedHandler = (handler: (() => void) | null): void => {
  unauthenticatedHandler = handler;
};

/**
 * Whether Engage rejected the request's session token. Other 401s, such as messenger not being
 * enabled, keep code "401" and are not session failures.
 * @param error The urql error from an operation result
 * @returns True when any GraphQL error carries the UNAUTHENTICATED code
 */
export const isUnauthenticatedError = (error?: CombinedError): boolean =>
  !!error?.graphQLErrors.some(graphQLError => {
    // urql keeps Engage's raw `{ message, code }` error as originalError.
    const original = graphQLError.originalError as { code?: unknown } | undefined;
    return (original?.code ?? graphQLError.extensions?.code) === UNAUTHENTICATED_CODE;
  });

// Create urql client with auth
export const graphqlClient = new Client({
  url: graphqlUrl,
  exchanges: [
    cacheExchange,
    mapExchange({
      onResult(result) {
        if (isUnauthenticatedError(result.error)) {
          unauthenticatedHandler?.();
        }
      },
    }),
    fetchExchange,
  ],
  fetchOptions: (): RequestInit => {
    const token = getAccessToken();
    return {
      method: "POST",
      headers: {
        authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include" as RequestCredentials,
    };
  },
  // Custom fetch to handle refreshToken mutation without auth header
  fetch: (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = getAccessToken();

    // Parse the request body to check if it's a refreshToken mutation
    let isRefreshMutation = false;
    if (init?.body && typeof init.body === "string") {
      try {
        const body = JSON.parse(init.body);
        isRefreshMutation = body.operationName === "refreshToken";
      } catch {
        // Ignore parse errors
      }
    }

    // Override headers if it's a refresh mutation
    const headers = new Headers(init?.headers);
    if (isRefreshMutation) {
      headers.delete("authorization");
    } else if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    return fetch(input, {
      ...init,
      headers,
    });
  },
  preferGetMethod: false,
});

// Re-export gql for defining queries/mutations
export { gql };

// ============================================
// GraphQL Mutations
// ============================================

export const CHANGE_NAME_MUTATION = gql`
  mutation ChangeName($input: ChangeUserNameInput!) {
    changeName(input: $input) {
      success
      message
      data {
        user_id
        first_name
        last_name
        middle_name
      }
    }
  }
`;

export const CHANGE_USER_EMAIL_MUTATION = gql`
  mutation ChangeUserEmail($input: ChangeEmailInput!) {
    changeUserEmail(input: $input) {
      CodeDeliveryDetailsList {
        AttributeName
        DeliveryMedium
        Destination
      }
    }
  }
`;

export const VERIFY_USER_EMAIL_MUTATION = gql`
  mutation VerifyUserEmail($input: VerifyEmailInput!) {
    verifyUserEmail(input: $input)
  }
`;

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input) {
      message
    }
  }
`;

export const DISABLE_MFA_MUTATION = gql`
  mutation DisableMfa($input: DisableMfaInput!) {
    disableMfa(input: $input) {
      success
      message
    }
  }
`;

export const LOGOUT_MUTATION = gql`
  mutation logout {
    logout {
      message
    }
  }
`;

export const GET_USER_RECORD_QUERY = gql`
  query getUserRecord($input: GetUserInput!) {
    getUserRecord(input: $input) {
      user_id
      account_id
      first_name
      last_name
      email
      profile_picture
      toc_completed
      pn_completed
      mfa {
        phone_number
        phoneVerified
        smsEnabled
        status
        totpEnabled
        emailEnabled
        __typename
      }
      roles {
        role_id
        role_name
        role_description
        rank
        scope
        __typename
      }
      fhir_metadata
      vitall_practitioner {
        fhir_practitioner_id
        user_id
        __typename
      }
      featuresv2
      __typename
    }
  }
`;

export const GET_USER_FEATURES = gql`
  query getUserFeatures($input: GetUserInput!) {
    userFeatures: getUserRecord(input: $input) {
      user_id
      email
      first_name
      last_name
      featuresv2
    }
  }
`;

export const UPDATE_CONSENT_MUTATION = gql`
  mutation updateUserVitallConsent($input: ChangeUserConsentInput!) {
    updateVitallConsent(input: $input) {
      message
      success
      data {
        user_id
        toc_completed
        pn_completed
      }
    }
  }
`;

export const REFRESH_TOKEN_MUTATION = gql`
  mutation refreshToken {
    refreshToken {
      id
      access
      user_id
      __typename
    }
  }
`;

export const POST_USER_RECORD_MUTATION = gql`
  mutation PostUserRecord($input: CreateUserModelInput!, $account_id: String!) {
    postUserRecord(input: $input, account_id: $account_id) {
      user_id
      account_id
      email
      first_name
      last_name
      fhir_metadata
      status
      mfa {
        phone_number
        phoneVerified
      }
      vitall_patient {
        vitall_patient_id
        patient_id
      }
      vitall_practitioner {
        fhir_practitioner_id
        user_id
      }
    }
  }
`;

export const UPDATE_ACCOUNT_MUTATION = gql`
  mutation UpdateAccountRecord($input: UpdateAccountModelInput!) {
    updateAccountRecord(input: $input) {
      success
      message
      data {
        account_id
        account_name
        phone_number
        email
        profile_picture
        # Account address fields
        account_address
        account_address2
        account_city
        account_country_id
        account_region_id
        account_zip_postal
      }
    }
  }
`;

export const GET_ACCOUNT_QUERY = gql`
  query GetAccountRecord($input: GetAccountInput!) {
    getAccountRecord(input: $input) {
      account_id
      account_name
      phone_number
      profile_picture
      # Account address fields (what we save to)
      account_address
      account_address2
      account_city
      account_country_id
      account_region_id
      account_zip_postal
    }
  }
`;

// ============================================
// Notification Preferences
// ============================================

export const RESEND_SETUP_INVITATION_MUTATION = gql`
  mutation ResendSetupInvitation($input: EmailInput!) {
    resendSetupInvitation(input: $input)
  }
`;

export const ADMIN_CHANGE_EMAIL_MUTATION = gql`
  mutation AdminChangeEmail($input: AdminChangeEmailInput!) {
    adminChangeEmail(input: $input) {
      user_id
      email
    }
  }
`;

export const GET_USER_NOTIFICATION_PREFERENCES_QUERY = gql`
  query getUserNotificationPreferences($input: GetUserNotificationsInput!) {
    getUserNotificationPreferences(input: $input) {
      identifier
      preference {
        user_id
        identifier
        email_enabled
      }
    }
  }
`;

export const UPDATE_USER_NOTIFICATION_PREFERENCE_MUTATION = gql`
  mutation updateUserNotificationPreference($input: UpdateNotificationPreferenceInput!) {
    updateUserNotificationPreference(input: $input) {
      identifier
      email_enabled
    }
  }
`;

// Self-only user preferences (e.g. weight_unit). Neither operation takes a user
// id: the backend always acts on the logged-in user.
export const GET_MY_USER_PREFERENCES_QUERY = gql`
  query getMyUserPreferences {
    getMyUserPreferences {
      key
      value
    }
  }
`;

export const UPDATE_MY_USER_PREFERENCE_MUTATION = gql`
  mutation updateMyUserPreference($input: UpdateMyUserPreferenceInput!) {
    updateMyUserPreference(input: $input) {
      key
      value
    }
  }
`;

// Admin-on-behalf-of variants: org admins read/edit another user's preferences.
// Backend reuses the self-edit input types (GetUserNotificationsInput /
// UpdateNotificationPreferenceInput); auth is enforced by the resolver, not
// the type.
export const ADMIN_GET_USER_NOTIFICATION_PREFERENCES_QUERY = gql`
  query adminGetUserNotificationPreferences($input: GetUserNotificationsInput!) {
    adminGetUserNotificationPreferences(input: $input) {
      identifier
      preference {
        user_id
        identifier
        email_enabled
      }
    }
  }
`;

export const ADMIN_UPDATE_USER_NOTIFICATION_PREFERENCE_MUTATION = gql`
  mutation adminUpdateUserNotificationPreference($input: UpdateNotificationPreferenceInput!) {
    adminUpdateUserNotificationPreference(input: $input) {
      identifier
      email_enabled
    }
  }
`;

// ============================================
// Practitioner Management (Admin)
// ============================================

export const GET_VITALL_PRACTITIONER_QUERY = gql`
  query getVitallPractitioner($input: GetVitallPractitionerInput!) {
    getVitallPractitioner(input: $input) {
      vitall_practitioner_id
      user_id
      fhir_practitioner_id
      deleted
      created_user_id
      created_at
      updated_at
    }
  }
`;

export const ADMIN_CHANGE_PHONE_MUTATION = gql`
  mutation adminChangePhone($input: AdminChangePhoneInput!) {
    adminChangePhone(input: $input) {
      user_id
      email
    }
  }
`;

// ============================================
// TypeScript Types
// ============================================

/**
 * Name change mutation input
 */
export interface ChangeUserNameInput {
  first_name: string;
  last_name: string;
  middle_name?: string;
  user_id: string;
}

/**
 * Name change mutation response
 */
export interface ChangeNameData {
  changeName: {
    success: boolean;
    message: string;
    data?: {
      user_id: string;
      first_name: string;
      last_name: string;
      middle_name?: string;
    };
  };
}

/**
 * Email change mutation input
 */
export interface ChangeEmailInput {
  newEmail: string;
}

/**
 * Code delivery details from email change
 */
export interface CodeDeliveryDetails {
  AttributeName?: string;
  DeliveryMedium?: string;
  Destination?: string;
}

/**
 * Email change mutation response
 */
export interface ChangeUserEmailData {
  changeUserEmail: {
    CodeDeliveryDetailsList?: CodeDeliveryDetails[];
  };
}

/**
 * Verify email mutation input
 */
export interface VerifyEmailInput {
  confirmationCode: string;
}

/**
 * Verify email mutation response
 */
export interface VerifyUserEmailData {
  verifyUserEmail: string;
}

/**
 * Password change mutation input
 */
export interface ChangePasswordInput {
  email: string;
  oldPassword: string;
  newPassword: string;
}

/**
 * Password change mutation response
 */
export interface ChangePasswordData {
  changePassword: {
    message: string;
  };
}

/**
 * Disable MFA mutation input
 */
export interface DisableMfaInput {
  method: string;
  code: string;
}

/**
 * Disable MFA mutation response
 */
export interface DisableMfaData {
  disableMfa: {
    success: boolean;
    message: string;
  };
}

export interface LogoutData {
  logout: {
    message: string;
  };
}

/**
 * GetUserInput for getUserRecord query
 */
export interface GetUserInput {
  user_id: string;
}

/**
 * User role from getUserRecord query
 */
export interface UserRole {
  role_id: string;
  role_name: string;
  role_description: string;
  rank: number;
  scope: string | null;
}

/**
 * GetUserRecord query response
 */
export interface GetUserRecordData {
  getUserRecord: {
    user_id: string;
    account_id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    profile_picture?: string;
    toc_completed?: boolean;
    pn_completed?: boolean;
    mfa?: {
      phone_number: string | null;
      phoneVerified: boolean;
      smsEnabled: boolean;
      status: string;
      totpEnabled: boolean;
      emailEnabled: boolean;
    };
    roles?: UserRole[];
    fhir_metadata?: string[];
    vitall_practitioner?: {
      fhir_practitioner_id: string;
      user_id: string;
    };
    featuresv2?: string[] | null;
  };
}

/**
 * GetUserRecord query response (features only)
 */
export interface GetUserFeaturesData {
  userFeatures: {
    user_id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    featuresv2: string[];
  };
}

/**
 * Consent update mutation input
 */
export interface ChangeUserConsentInput {
  user_id: string;
  toc_completed: boolean;
  pn_completed: boolean;
}

/**
 * Consent update mutation response
 */
export interface UpdateConsentData {
  updateVitallConsent: {
    message: string;
    success: boolean;
    data?: {
      user_id: string;
      toc_completed: boolean;
      pn_completed: boolean;
    };
  };
}

/**
 * RefreshToken mutation response
 */
export interface RefreshTokenData {
  refreshToken: {
    id: string;
    access: string;
    user_id: string;
    __typename: string;
  };
}

/**
 * Input for creating a patient user record
 */
export interface CreatePatientUserModelInput {
  sex?: string;
  gender?: string;
  date_of_birth?: string;
  mrn?: string;
  insurance_id?: string;
  primary_phone?: string;
  secondary_phone?: string;
  secondary_email?: string;
}

/**
 * Input for creating a practitioner user record
 */
export interface PractitionerRoleCodingInput {
  system: string;
  code: string;
  display: string;
}

export interface CreatePractitionerUserModelInput {
  primary_phone?: string;
  secondary_email?: string;
  organization_ids?: string[];
  provider_type?: PractitionerRoleCodingInput;
}

/**
 * Input for postUserRecord mutation
 */
export interface CreateUserModelInput {
  email?: string;
  // Can't send phone number in the main input because the backend expects it
  // in practitionerInputs for practitioners. phone_number doesn't exist on
  // CreateUserModelInput, and will lead to graphql validation errors if included.
  // phone_number?: string;
  first_name: string;
  last_name: string;
  title?: string;
  sex?: string;
  date_of_birth?: string;
  roles: string[];
  send_invite?: boolean;
  patientInputs?: CreatePatientUserModelInput;
  practitionerInputs?: CreatePractitionerUserModelInput;
}

/**
 * postUserRecord mutation response (UserModel)
 */
export interface PostUserRecordData {
  postUserRecord: {
    user_id: string;
    account_id: string;
    email?: string | null;
    first_name: string;
    last_name: string;
    title?: string | null;
    status?: string | null;
    fhir_metadata?: string[];
    mfa?: {
      phone_number: string | null;
      phoneVerified: boolean;
    };
    vitall_patient?: {
      vitall_patient_id: string;
      patient_id: string;
    } | null;
    vitall_practitioner?: {
      fhir_practitioner_id: string;
      user_id: string;
    } | null;
  };
}

/**
 * Input for updateAccountRecord mutation
 */
export interface UpdateAccountModelInput {
  account_id: string;
  account_name: string;
  account_address?: string;
  account_address2?: string;
  account_city?: string;
  account_region_id?: string;
  account_zip_postal?: string;
  account_country_id?: string;
  email?: string;
  phone_number?: string;
  profile_picture?: string;
}

/**
 * Account model from updateAccountRecord response
 */
export interface AccountModel {
  account_id: string;
  account_name: string;
  account_address?: string;
  account_address2?: string;
  account_city?: string;
  account_region_id?: string;
  account_zip_postal?: string;
  account_country_id?: string;
  phone_number?: string;
  email?: string;
  profile_picture?: string;
}

/**
 * updateAccountRecord mutation response
 */
export interface UpdateAccountData {
  updateAccountRecord: {
    success: boolean;
    message: string;
    data?: AccountModel;
  };
}

/**
 * Input for getAccountRecord query
 */
export interface GetAccountInput {
  account_id: string;
}

/**
 * Account data from getAccountRecord query
 */
export interface AccountRecord {
  account_id: string;
  account_name: string;
  phone_number?: string;
  profile_picture?: string;
  account_address?: string;
  account_address2?: string;
  account_city?: string;
  account_region_id?: string;
  account_country_id?: string;
  account_zip_postal?: string;
}

/**
 * getAccountRecord query response
 */
export interface GetAccountData {
  getAccountRecord: AccountRecord;
}

// ============================================
// Notification Preferences Types
// ============================================

/**
 * Input for getUserNotificationPreferences query
 */
export interface GetUserNotificationsInput {
  user_id: string;
}

/**
 * Input for updateUserNotificationPreference mutation
 */
export interface UpdateNotificationPreferenceInput {
  email_enabled: boolean;
  identifier: string;
  user_id: string;
}

/**
 * Notification preference for a user
 */
export interface NotificationPreference {
  user_id: string;
  identifier: string;
  email_enabled: boolean;
}

/**
 * Notification setting with user preference
 */
export interface NotificationSetting {
  identifier: string;
  preference: NotificationPreference;
}

/**
 * getUserNotificationPreferences query response
 */
export interface GetUserNotificationPreferencesData {
  getUserNotificationPreferences: NotificationSetting[];
}

/**
 * updateUserNotificationPreference mutation response
 */
export interface UpdateUserNotificationPreferenceData {
  updateUserNotificationPreference: {
    identifier: string;
    email_enabled: boolean;
  };
}

/**
 * One of the logged-in user's preferences. `value` is what to apply: the
 * user's stored choice, or the default when none is stored. The backend does
 * not say which.
 */
export interface UserPreference {
  key: string;
  value: string;
}

/**
 * getMyUserPreferences query response
 */
export interface GetMyUserPreferencesData {
  getMyUserPreferences: UserPreference[];
}

/** Input for updateMyUserPreference. The value must be one the key allows. */
export interface UpdateMyUserPreferenceInput {
  key: string;
  value: string;
}

/**
 * updateMyUserPreference mutation response
 */
export interface UpdateMyUserPreferenceData {
  updateMyUserPreference: UserPreference;
}

/**
 * adminGetUserNotificationPreferences query response.
 * Input type is the existing GetUserNotificationsInput — the user_id is the
 * target practitioner's Engage user_id (not the caller's). Authorization is
 * enforced server-side based on the caller's role.
 */
export interface AdminGetUserNotificationPreferencesData {
  adminGetUserNotificationPreferences: NotificationSetting[];
}

/**
 * adminUpdateUserNotificationPreference mutation response.
 * Input type is the existing UpdateNotificationPreferenceInput; user_id is
 * the target practitioner's Engage user_id.
 */
export interface AdminUpdateUserNotificationPreferenceData {
  adminUpdateUserNotificationPreference: {
    identifier: string;
    email_enabled: boolean;
  };
}

/**
 * Input for resendSetupInvitation mutation
 */
export interface EmailInput {
  email: string;
}

/**
 * resendSetupInvitation mutation response
 */
export interface ResendSetupInvitationData {
  resendSetupInvitation: boolean;
}

// ============================================
// Practitioner Management Types
// ============================================

/**
 * VitallPractitioner model from getVitallPractitioner query
 */
export interface VitallPractitionerModel {
  vitall_practitioner_id: string;
  user_id: string;
  fhir_practitioner_id: string;
  deleted: boolean;
  created_user_id?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Input for getVitallPractitioner query
 */
export interface GetVitallPractitionerInput {
  fhir_practitioner_id: string;
}

/**
 * getVitallPractitioner query response
 */
export interface GetVitallPractitionerData {
  getVitallPractitioner: VitallPractitionerModel;
}

/**
 * Input for adminChangePhone mutation
 */
export interface AdminChangePhoneInput {
  newPhone: string;
  user_id: string;
}

/**
 * adminChangePhone mutation response
 */
export interface AdminChangePhoneData {
  adminChangePhone: {
    user_id: string;
    email: string;
  };
}

/**
 * Input for adminChangeEmail mutation
 */
export interface AdminChangeEmailInput {
  currentEmail: string;
  newEmail: string;
}

/**
 * adminChangeEmail mutation response (returns updated UserModel)
 */
export interface AdminChangeEmailData {
  adminChangeEmail: {
    user_id: string;
    email: string;
  };
}
